/**
 * crash-center SaaS 错误监控契约（Task 7 + P2 Task 5），逐字段对齐后端
 * renren-crash-center 的 CrashIssue / CrashIssuePageView / CrashIssueDetailView
 * (+EventSummary/StackFrame/CrashRetracedFrame) / CrashStatsTrendView record
 * （SaasCrashIssueController 三端点 + SaasCrashStatsController trend 端点）。
 *
 * 服务端 Jackson 把 Long 统一序列化为字符串（平台级 WebStarterAutoConfiguration 的
 * jacksonLongToStringCustomizer，与 dimension-analytics 同坑），而本契约的数值字段
 * 声明为 number：tenantId / eventCount / userCount / droppedCount / assigneeId /
 * total / 趋势 events / users / totalEvents / totalUsers 在取数边界统一收敛为
 * number。status 为 V366 CHECK 同集的状态机 {NEW, PROCESSING, RESOLVED, IGNORED}
 * （服务层校验，H2 无 CHECK 兜底）。StackFrame 的 line/column 为 Integer（非 Long），
 * 服务端按 number 序列化，无需收敛。
 */

/** issue 状态机合法值（后端 CrashIssueQueryService.STATUSES / V366 CHECK 同集）。 */
export const CRASH_ISSUE_STATUSES = ['NEW', 'PROCESSING', 'RESOLVED', 'IGNORED'] as const
export type CrashIssueStatus = (typeof CRASH_ISSUE_STATUSES)[number]

/** 趋势 period 合法值（后端 CrashStatsService.PERIODS 同集，大小写敏感）。 */
export const CRASH_STATS_TREND_PERIODS = ['YESTERDAY', 'LAST_7_DAYS', 'LAST_30_DAYS'] as const
export type CrashStatsTrendPeriod = (typeof CRASH_STATS_TREND_PERIODS)[number]

/**
 * 错误监控 view 级权限码（后端 CrashPermissions.CRASH_ISSUES_VIEW，V367 种子；
 * 列表 / 详情 / 状态流转三端点共用，spec 开放问题 1 的 P1 裁决）。
 */
export const CRASH_ISSUES_VIEW_PERMISSION = 'system:crash:issues:view'

/** crash_issue 聚合快照（后端 CrashIssue record 同构，查询侧只读投影）。 */
export interface CrashIssueView {
  tenantId: number
  /** 指纹 char(64) hex，issue 主键与详情/流转路径参数。 */
  fingerprint: string
  title: string
  platform: string
  appVersion: string
  exceptionType: string
  /** Instant → ISO-8601 字符串。 */
  firstSeenAt: string
  lastSeenAt: string
  /** 实际入库事件数（不含限流丢弃）。 */
  eventCount: number
  /** 影响用户数（anonymous_id 去重）。 */
  userCount: number
  /** 限流丢弃数（风暴日饱和失真口径：B 端次数列 = eventCount + droppedCount）。 */
  droppedCount: number
  status: CrashIssueStatus
  /** 处理人用户 ID；null = 未指派。 */
  assigneeId: number | null
}

/** issue 列表分页视图（items 按 last_seen_at desc, fingerprint 排序）。 */
export interface CrashIssuePageView {
  items: CrashIssueView[]
  /** 过滤后、分页前总数（Long，取数边界归一化为 number）。 */
  total: number
  page: number
  size: number
}

/** 堆栈原始帧（后端 CrashEventContracts.StackFrame 同构；symbol 可空的契约位保留）。 */
export interface CrashStackFrameView {
  symbol: string | null
  file: string | null
  /** Integer → number，服务端不串化；无值 null。 */
  line: number | null
  column: number | null
  /** 帧级平台标记（dart/jvm 等，与事件 platform 不同口径）。 */
  platform: string | null
}

/**
 * 还原帧（后端 P2 Task 5 CrashRetracedFrame：R8 RetracedFrame / SM ResolvedFrame
 * 的统一扁平投影）。原始帧五字段 + resolved 标记 + 还原四字段；resolved=false 时
 * 还原字段全 null，展示层逐帧回退原始（T3/T4 回退语义）。
 */
export interface CrashRetracedFrameView extends CrashStackFrameView {
  resolved: boolean
  /** R8=还原类.方法；sourcemap=names 命中名（帧无名时 null）。 */
  resolvedSymbol: string | null
  resolvedFile: string | null
  resolvedLine: number | null
  /** 仅 sourcemap 产出；R8 无列概念恒 null。 */
  resolvedColumn: number | null
}

/** 单条事件摘要（后端 CrashIssueDetailView.EventSummary；message 已截断 200 字）。 */
export interface CrashEventSummaryView {
  occurredAt: string
  platform: string
  os: string
  appVersion: string
  /** 符号文件键组成（crash_event.app_build 落库已归一空串）。 */
  appBuild: string
  anonymousId: string
  exceptionMessage: string
  /** 堆栈首帧 symbol；无堆栈 / 无 symbol 时 null。 */
  stackFirstFrame: string | null
  /** 原始帧全列表（契约侧 ≤100 帧已限；缺省归一为空数组）。 */
  stack: CrashStackFrameView[]
  /** 还原帧；null = 无符号文件 / 无堆栈 / 坏图（仅最新 1 条事件携带，P2 简化）。 */
  retracedStack: CrashRetracedFrameView[] | null
}

/** issue 详情视图：issue 投影 + 最新 10 条 event 摘要（occurred_at 倒序）。 */
export interface CrashIssueDetailView {
  issue: CrashIssueView
  recentEvents: CrashEventSummaryView[]
}

/** 趋势单日点（后端 CrashStatsTrendView.DailyPoint；无数据日补零）。 */
export interface CrashStatsTrendPointView {
  /** LocalDate → ISO 日期字符串（UTC 日窗）。 */
  localDate: string
  events: number
  users: number
}

/** 崩溃趋势视图（后端 CrashStatsTrendView，P2 Task 1 端点 / P2 Task 5 镜像）。 */
export interface CrashStatsTrendView {
  period: CrashStatsTrendPeriod
  fromDate: string
  toDate: string
  /** 按 localDate 升序、长度恒等于窗口天数（补零）。 */
  series: CrashStatsTrendPointView[]
  totalEvents: number
  totalUsers: number
  days: number
}

/** 列表查询参数（与 SaasCrashIssueController 的 page/size/status/keyword 一一对应）。 */
export interface CrashIssueListQuery {
  page?: number
  size?: number
  /** null = 全部状态（请求省略 status 参数）。 */
  status?: CrashIssueStatus | null
  /** 空白即省略（后端 trim 后跳过）。 */
  keyword?: string | null
}

/** 状态流转请求体（后端 StatusUpdateRequest；assigneeId 缺省 = 处理人保持现值）。 */
export interface CrashStatusUpdateRequest {
  status: CrashIssueStatus
  /** 传 null/undefined 均省略该字段；P1 无解除指派入口。 */
  assigneeId?: number | null
}

/** 趋势查询参数（period 必传；appVersion/platform 为可选精确过滤，空白即省略）。 */
export interface CrashStatsTrendQuery {
  period: CrashStatsTrendPeriod
  appVersion?: string | null
  platform?: string | null
}

/** 错误监控查询/流转所需的 api 形态（ApiClient 满足；测试可注入 fake）。 */
export interface CrashIssueApi {
  get<T>(path: string, init?: RequestInit): Promise<T>
  put<T>(path: string, body?: unknown, init?: RequestInit): Promise<T>
}

/** 列表默认页大小（后端 CrashIssueQueryService.DEFAULT_PAGE_SIZE）。 */
export const CRASH_ISSUE_DEFAULT_PAGE_SIZE = 20

/**
 * 服务端 Jackson 将 Long 统一序列化为字符串，而契约字段为 number——取数边界统一
 * 收敛，避免消费方 `Number.isFinite("12") === false` 把指标渲染成 '-'。
 */
function toCount(value: number | string): number {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function normalizeIssue(issue: CrashIssueView): CrashIssueView {
  return {
    ...issue,
    tenantId: toCount(issue.tenantId),
    eventCount: toCount(issue.eventCount),
    userCount: toCount(issue.userCount),
    droppedCount: toCount(issue.droppedCount),
    assigneeId:
      issue.assigneeId === null || issue.assigneeId === undefined ? null : toCount(issue.assigneeId)
  }
}

/**
 * 事件摘要缺省归一：stack 缺失 → 空数组；retracedStack 保留 null 语义
 * （null = 无图/无堆栈，B 端仅展示原始帧）。
 */
function normalizeEventSummary(event: CrashEventSummaryView): CrashEventSummaryView {
  return {
    ...event,
    appBuild: event.appBuild ?? '',
    stack: event.stack ?? [],
    retracedStack: event.retracedStack ?? null
  }
}

/**
 * 拼接列表查询串：page/size 恒传（默认 1/20）；status 为 null（全部状态）时省略；
 * keyword trim 后为空即省略（与后端"空白跳过"口径一致）。
 */
function buildCrashIssueListQuery(query: CrashIssueListQuery): string {
  const params = new URLSearchParams()
  params.set('page', String(query.page ?? 1))
  params.set('size', String(query.size ?? CRASH_ISSUE_DEFAULT_PAGE_SIZE))
  if (query.status) {
    params.set('status', query.status)
  }
  const keyword = query.keyword?.trim() ?? ''
  if (keyword) {
    params.set('keyword', keyword)
  }
  return params.toString()
}

/** GET /api/saas/crash/issues：issue 列表（分页 + 可选 status/keyword 过滤）。 */
export async function fetchCrashIssues(
  api: CrashIssueApi,
  query: CrashIssueListQuery = {}
): Promise<CrashIssuePageView> {
  const payload = await api.get<CrashIssuePageView>(
    `/api/saas/crash/issues?${buildCrashIssueListQuery(query)}`
  )
  return {
    ...payload,
    items: (payload.items ?? []).map(normalizeIssue),
    total: toCount(payload.total)
  }
}

/** GET /api/saas/crash/issues/{fingerprint}：详情 + 最新 10 条 event 摘要。 */
export async function fetchCrashIssueDetail(
  api: CrashIssueApi,
  fingerprint: string
): Promise<CrashIssueDetailView> {
  const payload = await api.get<CrashIssueDetailView>(
    `/api/saas/crash/issues/${encodeURIComponent(fingerprint)}`
  )
  return {
    issue: normalizeIssue(payload.issue),
    recentEvents: (payload.recentEvents ?? []).map(normalizeEventSummary)
  }
}

/**
 * GET /api/saas/crash/stats/trend：日序列（补零）+ 顶层汇总。Long 计数
 * （series[].events/users、totalEvents/totalUsers）取数边界收敛为 number。
 */
export async function fetchCrashStatsTrend(
  api: CrashIssueApi,
  query: CrashStatsTrendQuery
): Promise<CrashStatsTrendView> {
  const params = new URLSearchParams()
  params.set('period', query.period)
  const appVersion = query.appVersion?.trim() ?? ''
  if (appVersion) {
    params.set('appVersion', appVersion)
  }
  const platform = query.platform?.trim() ?? ''
  if (platform) {
    params.set('platform', platform)
  }
  const payload = await api.get<CrashStatsTrendView>(
    `/api/saas/crash/stats/trend?${params.toString()}`
  )
  return {
    ...payload,
    series: (payload.series ?? []).map((point) => ({
      ...point,
      events: toCount(point.events),
      users: toCount(point.users)
    })),
    totalEvents: toCount(payload.totalEvents),
    totalUsers: toCount(payload.totalUsers),
    days: toCount(payload.days)
  }
}

/** PUT /api/saas/crash/issues/{fingerprint}/status：状态流转，返回更新后的 issue 快照。 */
export async function updateCrashIssueStatus(
  api: CrashIssueApi,
  fingerprint: string,
  request: CrashStatusUpdateRequest
): Promise<CrashIssueView> {
  const body: { status: CrashIssueStatus; assigneeId?: number } = { status: request.status }
  if (request.assigneeId !== undefined && request.assigneeId !== null) {
    body.assigneeId = request.assigneeId
  }
  const payload = await api.put<CrashIssueView>(
    `/api/saas/crash/issues/${encodeURIComponent(fingerprint)}/status`,
    body
  )
  return normalizeIssue(payload)
}
